// https://stripe.com/docs/payments/accept-card-payments?platform=web&ui=elements&html-or-react=react
import useDebouncedInput from "../hooks/useDebouncedInput";
import backendApi, {
  useChargeCentsMutation,
  useGetPaymentMethodsQuery,
  useGetProductsQuery,
  useSchedulePaymentMutation,
} from "../store/api";
import { RT_SCHEDULEDPAYMENT } from "../store/constants";
import AuthContext from "../store/AuthContext";
import { selectDeal, selectEmail } from "../store/cashier";
import { getPaymentMethod } from "../util/misc";
import { getAddressOnly } from "../util/smarty";
import {
  cardInfoString,
  checkCardValidity,
  dateToIso8601YYYYMMDD,
  explainDecline,
  formatCents,
  iso8601YYYYMMDDtoLocalDate,
  ordinal_suffix_of,
  phoenixTime,
} from "../util/stripe";
import { CalendarIcon as BsCalendarDate } from "@heroicons/react/20/solid";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { addDays, addMonths } from "date-fns";
import { Field, Form, Formik } from "formik";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { ImCheckmark, ImCross, ImWarning } from "react-icons/im"; // stripe setup

import { useDispatch, useSelector } from "react-redux";
import { twMerge } from "tailwind-merge";
import * as Yup from "yup";

import AddressSelector from "./AddressSelector";
import CardManagement from "./CardManagement";
import CohortInfo from "./CohortInfo";
import FieldGroup from "./FieldGroup";
import OverlayDialog from "./OverlayDialog";
import ProductDropdown from "./ProductDropdown";
import StyledErrorMessage from "./StyledErrorMessage";
import StyledFormikField from "./StyledFormikField";
import TWDatePicker from "./TWDatePicker";
import VerbalAgreement from "./VerbalAgreement";

// eslint-disable-next-line no-undef
const sandbox = process.env.REACT_APP_sandbox || false;

const validateEntry = (entries, id) => {
  if (!id) {
    return id;
  }
  if (entries.map((entry) => String(entry.id)).includes(id)) {
    return id;
  }
  return ""; // no matching entry
};

const PaymentForm = (props) => {
  // bad card 5128 1234 5678 9010
  // ok card 5128 1234 5678 9011
  // 4242424242424242 succeed+process
  // 4000000000009995 fail insufficient_funds
  // 4000002500003155 authentication required (fail authentication_not_handled)

  const { setMessage } = props;

  const stripe = useStripe();
  const elements = useElements();

  const authctx = useContext(AuthContext);
  const { setOverlay } = authctx;

  const dispatch = useDispatch();
  const [schedulePayment] = useSchedulePaymentMutation();
  const [chargeCents] = useChargeCentsMutation();

  const { deal, stripeCustomer, stripeCustomerId } = useSelector(
    (state) => state.cashier,
  );
  const { user } = useSelector((state) => state.app);

  const { data: cards = [] } = useGetPaymentMethodsQuery(
    { customerId: stripeCustomerId },
    { skip: !stripeCustomerId },
  );

  const address = getAddressOnly(deal);

  const formRef = useRef();

  const defaultPaymentMethod =
    stripeCustomer?.invoice_settings?.default_payment_method ||
    stripeCustomer?.default_source ||
    ""; // new or old API
  // both Source ids (src_xxx) and Payment Method ids (pm_xxx) work as payment methods in the new API

  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState(defaultPaymentMethod);
  const [cardWillBeValid, setCardWillBeValid] = useState(false);

  const [products, setProducts] = useState([]);
  const [stripeProductId, setStripeProductId] = useState(null);
  const [cohortRequired, setCohortRequired] = useState(false);

  const [doneDialog, setDoneDialog] = useState(false);

  const [previewTax, setPreviewTax] = useState(0);
  const tellTax = (taxDollars) => {
    setPreviewTax(() => taxDollars * 100);
  };

  // derived state
  const getproductbyid = useCallback(
    (selected) => {
      return products.find((p) => p.id === selected);
    },
    [products],
  );

  let { preloadProduct } = props;
  const [selectedProduct, setSelectedProduct] = useState(preloadProduct || "");

  // calculate stripe product id when selected product changes
  useEffect(() => {
    if (!selectedProduct || !products?.length) {
      return;
    }
    const selectedProd = getproductbyid(selectedProduct);

    setCohortRequired(() => selectedProd?.cohort_date === "required");

    const stripeProductId =
      (products || []).find((p) => p.id === selectedProd?.id)?.product_id ||
      null;

    setStripeProductId(() => stripeProductId);
  }, [
    selectedProduct,
    products,
    stripeProductId,
    setStripeProductId,
    getproductbyid,
  ]);

  // handlers, functions
  const handleProductChange = (prod) => {
    setSelectedProduct(() => prod);
  };

  const handleCharge = async (event) => {
    if (event) {
      event.preventDefault();
    }
    if (!stripe) {
      return;
    }

    if (formRef.current.values.schedule) {
      return scheduleCharge();
    }

    chargeNow();
  };

  const chargeNow = async () => {
    try {
      setMessage({
        ok: false,
        text: ``,
      });

      const customerId = stripeCustomer.id;

      let purchase;

      const amount = +formRef.current.values.amount * 100; // we pay in minimum fraction = cents
      const buyThisProduct = getproductbyid(selectedProduct);
      const productName = buyThisProduct.name;
      setOverlay(() => "Paying product…");

      // if we want to use customer balance with payment intents, it must be done manually:
      // need to check balance and
      // 1) "charge" all by negatively crediting user, if credit is large enough, or
      // 2) try charging the _difference_ only, and if successful, zero out the user credit balance.

      // this is direct full price purchase. Tax will be added by backend according to the supplied address and product
      const card = getPaymentMethod(cards, selectedPaymentMethod)?.card || {};

      const { data: purchaseData, error: purchaseError } = await chargeCents({
        customerId,
        paymentMethodId: selectedPaymentMethod,
        amount,
        description: productName,
        address,
        card_info: cardInfoString(card?.card || {}),
        customer_email: stripeCustomer.email,
        customer_name: stripeCustomer.name,
        term_description: "one off payment",
        has_tax: buyThisProduct.has_tax === "Yes",
        metadata: {
          deal_id: deal.id,
          rep_id: user.repid,
          product_id: selectedProduct,
          stripe_product_id: stripeProductId,
          processor: "gateway",
          ...(formRef.current.values.cohort || cohortRequired
            ? {
                cohort_year: formRef.current.values.cohort_year,
                cohort_month: formRef.current.values.cohort_month,
              }
            : null),
        },
      });

      purchase = purchaseData;

      setOverlay(() => "");

      if (purchaseError) {
        throw new Error(JSON.stringify(purchaseError));
      }

      switch (purchase.status) {
        case "succeeded": {
          const msg = {
            ok: true,
            text: `The customer was successfully charged.`,
          };
          setMessage(msg); // form wrapper
          setDoneDialog(() => msg);
          break;
        }
        case "active": {
          const msg = {
            ok: true,
            text: `The customer was successfully charged, and the subscription is active.`,
          };
          setMessage(msg);
          setDoneDialog(msg);

          break;
        }
        default: {
          const msg = {
            ok: true,
            text: `The payment was approved with unknown status "${purchase.status}"`,
          };
          setMessage(msg);
          setDoneDialog(msg);
        }
      }
      clearForm(); // don't clear the ok/fail user messages
    } catch (ex) {
      const err =
        typeof ex?.message === "object" ? JSON.parse(ex.message) : ex.message;

      setOverlay(() => "");

      setMessage(() => ({
        ok: false,
        text: explainDecline(err),
      }));
    }
  };

  const scheduleCharge = async () => {
    // do this for x repetitions on addDate(firstchange, repetitions)

    try {
      setMessage({
        ok: false,
        text: ``,
      });
      const customerId = stripeCustomer.id;

      let purchase;

      const amount = +formRef.current.values.amount * 100; // we pay in minimum fraction = cents
      const repetitions = Math.round(+formRef.current.values.repetitions); // 1 or more
      const card = getPaymentMethod(cards, selectedPaymentMethod) || {};
      let scheduleTime = formRef.current.values.payment_date; // 1st

      // multiple schedules
      const warnings = [];
      let scheduledPayments = 0;
      const buyThisProduct = getproductbyid(selectedProduct);

      for (let rep = 1; rep <= repetitions; rep++) {
        const cardWillBeValid = checkCardValidity(
          card,
          addMonths(new Date(scheduleTime), rep - 1), // start by adding 0 months
        );

        let term_description =
          repetitions > 1 ? `Payment ${rep}/${repetitions}` : "One off payment";

        const payment_date = dateToIso8601YYYYMMDD(
          addMonths(iso8601YYYYMMDDtoLocalDate(scheduleTime), rep - 1),
        );
        const cohortInfo =
          formRef.current.values.cohort || cohortRequired
            ? {
                cohort_year: formRef.current.values.cohort_year,
                cohort_month: formRef.current.values.cohort_month,
              }
            : null;
        const paymentData = {
          customer_id: customerId,
          subscription: false,
          rep_id: user.repid,
          deal_id: deal.id,
          product_id: selectedProduct,
          amount_cents: amount,
          product_name: buyThisProduct.name,
          payment_date,
          customer_email: stripeCustomer.email,
          customer_name: stripeCustomer.name,
          card_info: cardInfoString(card?.card || {}),
          payment_method: selectedPaymentMethod,
          term_description,
          status: cardWillBeValid ? "scheduled" : "unscheduled",
          sandbox,
          has_tax: buyThisProduct.has_tax === "Yes",
          stripe_product_id: stripeProductId, // alter table scheduled_payments add column stripe_product_id varchar(45);
          ...cohortInfo, // alter table scheduled_payments add column cohort_year int null;
          // alter table scheduled_payments add column cohort_month int null;
        };

        setOverlay(() => `Scheduling ${term_description}`);

        const { data: purchaseresult, error: purchaseError } =
          await schedulePayment(paymentData);
        purchase = purchaseresult;

        if (!cardWillBeValid) {
          warnings.push(`The card will be invalid for ${term_description}`);
        }
        scheduledPayments++;

        if (purchaseError) {
          throw new Error(purchaseError.message);
        }
      }

      // invalidate after all payments are written, so the list refetches once with all results
      dispatch(backendApi.util.invalidateTags([RT_SCHEDULEDPAYMENT]));

      setOverlay(() => "");

      const dateOptions = {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      };

      const dates =
        repetitions === 1
          ? `for ${new Date(purchase.payment_date).toLocaleDateString(
              "en-US",
              dateOptions,
            )}`
          : `on the ${ordinal_suffix_of(
              +(scheduleTime?.split("-") || [])[2],
            )} each month`;

      const taxMention =
        buyThisProduct.has_tax === "Yes"
          ? `Tax (approximately ${formatCents(
              previewTax,
            )}) will be added to each charge.`
          : "No tax on this purchase.";

      let text = `${scheduledPayments}× ${formatCents(
        amount,
      )} payment${
        scheduledPayments > 1 ? "s" : ""
      } scheduled ${dates}, on card ${cardInfoString(card?.card || {})}. ${taxMention}`;

      // add warnings here

      const msg = {
        ok: true,
        text,
      };
      if (warnings.length) {
        msg.warningMessage = `NOTE: ${warnings.join(", ")}`;
      }
      setMessage(msg);
      setDoneDialog(msg);

      // clearForm(); // don't clear the ok/fail user messages
    } catch (err) {
      setOverlay(() => "");

      if (err.message.includes("error_on_requires_action")) {
        setMessage({
          ok: false,
          text: `Unsupported card. The card may be valid but requires 2 factor authentication. Try another card.`,
        });
      } else {
        setMessage({
          ok: false,
          text: `${err.message}`,
        });
      }
    }
  };

  const clearForm = (event) => {
    if (event) {
      event.preventDefault();
    }

    props.formClean(true);

    formRef.current.resetForm();
  };

  const clearFormButtonHandler = (event) => {
    if (event) {
      event.preventDefault();
    }
    setMessage({ ok: true, text: "" }); // no message is … no message
    clearForm();
  };
  const [debouncedAmount, setDebouncedAmount] = useDebouncedInput();

  const [calendarOpen, setCalendarOpen] = useState(false);

  const { data: { stripePrices: rawProducts } = {} } = useGetProductsQuery();

  useEffect(() => {
    const newProds = (rawProducts ?? []).filter(
      (p) => !p.hs_recurring_billing_period,
    );
    setProducts(() => newProds);

    // if product list changes and the selected product is no longer there
    if (newProds && newProds.length && selectedProduct) {
      const validatedProduct = validateEntry(newProds, selectedProduct);
      if (!validatedProduct) {
        setSelectedProduct(() => "");
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawProducts]);

  const [chargeTax, setChargeTax] = useState(false);

  // watch product selection
  useEffect(() => {
    const selectedProd = getproductbyid(selectedProduct);
    setChargeTax(() => selectedProd?.has_tax === "Yes");
    if (!selectedProd?.product_id) {
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  const scheduleToggled = (event) => {
    const newstate = event.target.checked;

    formRef.current.setFieldValue("schedule", newstate);
    if (newstate && !formRef.current.values.payment_date) {
      formRef.current.setFieldValue(
        "payment_date",
        dateToIso8601YYYYMMDD(addDays(phoenixTime(), 1)),
      );
    }
    setCalendarOpen(() => newstate);
  };

  const clearCustomerSelection = () => {
    dispatch(selectEmail(""));
    dispatch(selectDeal(null));
  };

  return (
    <>
      <Formik
        innerRef={formRef}
        initialValues={{
          amount: "",
          agree: "",
          addCredit: false,
          creditAmount: 0,
          schedule: false,
          repetitions: 1,
          cohort: false,
          cohort_year: null,
          cohort_month: null,
        }}
        validationSchema={Yup.object({
          amount: Yup.number()
            .typeError("Must be number")
            .positive("Must be positive")
            .required("Amount is required")
            .min(0.5, "Minimum 50 cents"),

          creditAmount: Yup.number()
            .typeError("Must be number")
            .positive("Must be positive")
            .min(0.01, "Must be at least 1 cent"),

          agree: Yup.boolean().oneOf([true]),
          schedule: Yup.boolean().oneOf([true, false]),
          addCredit: Yup.boolean().oneOf([true, false]),
          payment_date: Yup.string(),
          repetitions: Yup.number()
            .typeError("Must be number")
            .positive("Must be positive")
            .integer("Must be whole numbers")
            .min(1, "Must be at least 1 charge"),
        })}
      >
        {(form) => {
          const { errors, touched, values } = form;

          // check if this card will be valid in the future
          touched.payment_date = true;

          // form internal, set payment_date form field. gets Date object from datepicker
          const setDate = (datestr) => {
            form.setFieldValue("payment_date", datestr);
          };

          const chargeDisabled =
            !values.agree ||
            (values.schedule &&
              values.repetitions < 2 &&
              (typeof values.payment_date === "undefined" ||
                !cardWillBeValid)) || // ass-u-me datepicker will ensure only future dates. Only barf when it's the only payment that can fail
            (values.addCredit &&
              (errors.creditAmount || values.creditAmount < 0.01)) ||
            !selectedProduct ||
            !selectedProduct === "undefined" ||
            !selectedPaymentMethod ||
            !getPaymentMethod(cards, selectedPaymentMethod) ||
            errors.repetitions ||
            selectedPaymentMethod === "new" ||
            errors.amount ||
            (chargeTax && !address?.address) || // only require address if we are charging tax on selected product
            (!values.amount && !touched.amount) ||
            (cohortRequired && (!values.cohort_year || !values.cohort_month));

          // Products dropdown
          const listProducts = products.filter(
            (p) => !p.hs_recurring_billing_period,
          );

          // Prices dropdown
          // list calculated in state

          const repetitionText =
            values.repetitions > 1
              ? ` and thereafter on the ${ordinal_suffix_of(
                  +(values.payment_date?.split("-") || [])[2],
                )} each month`
              : "";

          const chargeText = values.schedule
            ? `Charged on ${iso8601YYYYMMDDtoLocalDate(
                values.payment_date,
              ).toLocaleDateString(undefined, {
                day: "numeric",
                month: "short",
              })}${repetitionText}`
            : "Charged today";

          const setAmount = (value) => {
            // formRef.current.setFieldValue("amount", event.target.value);
            setDebouncedAmount(value || 0);
          };

          // we get goodies from Formik that we can use here
          return (
            <Form className="px-4" onSubmit={handleCharge}>
              <ProductDropdown
                selected={selectedProduct}
                onChange={handleProductChange}
                products={listProducts}
                labelWithPrice
              />

              <CardManagement
                cards={cards}
                elements={elements}
                paymentDate={values?.payment_date}
                selectedPaymentMethod={selectedPaymentMethod}
                setCardWillBeValid={setCardWillBeValid}
                setSelectedPaymentMethod={setSelectedPaymentMethod}
                stripe={stripe}
                stripeCustomer={stripeCustomer}
              />

              <div className={twMerge("flex flex-col")}>
                <label htmlFor="deal" className="my-1 mt-4 text-blue-400">
                  Amount
                </label>
                <StyledFormikField
                  type="text"
                  className={twMerge("p-2")}
                  name="amount"
                  tellChange={setAmount}
                />
                {errors?.amount && touched?.amount && (
                  <StyledErrorMessage name="amount" />
                )}
              </div>

              {/* Schedule payments input field */}
              <div className="mb-2 mt-4 text-blue-400">
                Schedule new payment
              </div>
              <div className="flex flex-col">
                <div className="flex flex-row items-center pt-2 text-justify text-sm text-gray-400">
                  <div className="grow text-left">
                    <Field
                      type="checkbox"
                      id="scheduledCheckbox"
                      name="schedule"
                      className="mx-1 cursor-pointer px-1"
                      onChange={scheduleToggled}
                    />
                    <label
                      htmlFor="scheduledCheckbox"
                      className="mx-1 cursor-pointer px-1"
                    >
                      {`Schedule payment ${
                        values.schedule && values.repetitions > 1
                          ? "starting: " +
                            (values.payment_date || "(select a date)")
                          : values.schedule
                            ? "for: " +
                              (values.payment_date || "(select a date)")
                            : "for a later date"
                      }`}
                    </label>
                  </div>
                  {values.schedule && (
                    <div
                      className="cursor-pointer"
                      onClick={() => setCalendarOpen(() => !calendarOpen)}
                    >
                      <BsCalendarDate className="size-5" aria-hidden="true" />
                    </div>
                  )}
                </div>
                <div className="flex flex-col">
                  {calendarOpen && (
                    <TWDatePicker
                      className="self-center"
                      onChange={setDate}
                      onClose={() => setCalendarOpen(() => false)}
                      minDate={addDays(phoenixTime(), 1)}
                      defaultDate={values.payment_date}
                    />
                  )}
                </div>
              </div>
              {cohortRequired && <CohortInfo required={cohortRequired} />}

              {!cardWillBeValid && selectedPaymentMethod !== "new" && (
                <div className="my-2 flex bg-amber-100 p-2 text-center text-amber-700">
                  <ImWarning />
                  The selected card will be invalid for one or more of the
                  installments.
                </div>
              )}

              {values.schedule && (
                <div className={twMerge("flex flex-col")}>
                  <label htmlFor="deal" className="my-1 mt-4 text-blue-400">
                    {`Monthly repetitions on the ${ordinal_suffix_of(
                      +(values.payment_date?.split("-") || [])[2],
                    )} each
                  month`}
                  </label>
                  <StyledFormikField
                    type="text"
                    className={twMerge("p-2")}
                    name="repetitions"
                  />
                  {errors?.repetitions && (
                    <StyledErrorMessage name="repetitions" />
                  )}
                </div>
              )}

              {deal && (
                <div className="my-2 flex w-full flex-row text-gray-700">
                  Remaining balance:{" "}
                  {formatCents(
                    parseInt(getproductbyid(selectedProduct)?.priceCents || 0) -
                      values.amount * 100 -
                      parseFloat(deal?.amount || 0) * 100,
                  )}
                </div>
              )}

              {/* Address and tax display */}
              <AddressSelector
                product={getproductbyid(selectedProduct)}
                chargeText={chargeText}
                chargeAmount={debouncedAmount}
                tellTax={tellTax}
              />

              <VerbalAgreement />

              <FieldGroup className="content-center">
                <button
                  disabled={chargeDisabled}
                  className={twMerge(
                    "my-2 w-full p-2 font-semibold text-white",
                    chargeDisabled ? "bg-gray-400" : "bg-blue-400",
                  )}
                >
                  {values.schedule ? "Schedule payment" : "Charge"}
                </button>
              </FieldGroup>
              {doneDialog && (
                <OverlayDialog
                  onYes={() => setDoneDialog(() => false)}
                  onNo={clearCustomerSelection}
                  ok={doneDialog.ok}
                  icon={doneDialog.ok ? ImCheckmark : ImCross}
                  message={doneDialog.text}
                  warningMessage={doneDialog.warningMessage}
                  message2={"Keep working with this customer?"}
                />
              )}

              <FieldGroup className="content-center">
                <button
                  className="my-2 w-full rounded border border-blue-500 p-2 font-semibold text-blue-400"
                  onClick={clearFormButtonHandler}
                >
                  Clear
                </button>
              </FieldGroup>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default PaymentForm;
